import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ShareProposal from "@/pages/ShareProposal";
import Exports from "@/pages/Exports";
import PublicProposal from "@/pages/PublicProposal";
import ContractReview from "@/pages/ContractReview";
import Pipeline from "@/pages/Pipeline";
import Revenue from "@/pages/Revenue";
import QuarterGlance from "@/pages/QuarterGlance";
import QuarterPublic from "@/pages/QuarterPublic";
import Activity from "@/pages/Activity";
import SowPreview from "@/pages/SowPreview";
import SowEmails from "@/pages/SowEmails";
import StaffRfqs from "@/pages/StaffRfqs";
import SupplierRFQ from "@/pages/SupplierRFQ";
import Login from "@/pages/Login";
import { LegalEula, LegalPrivacy } from "@/pages/Legal";
import Team from "@/pages/Team";
import BoardPack from "@/pages/BoardPack";
import Announcements from "@/pages/Announcements";
import { AuthProvider } from "@/lib/auth";
import { DraftJobProvider } from "@/lib/draftJob";
import ProtectedRoute from "@/lib/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";

// Wrap any staff route in <ProtectedRoute>. Wrapper lives in
// ``/app/frontend/src/lib/ProtectedRoute.jsx``, the gate is:
//   - render a "Checking session…" splash while ``GET /api/auth/me``
//     is in flight on first mount,
//   - redirect to /login?from=<original-url> when unauthenticated,
//   - render the page when authenticated (and ``role==='admin'`` if
//     ``adminOnly`` is on).
// Public routes (/login, /p/:token, /q/:token) are not wrapped - a
// client whose token was redacted out of the URL lands on /login
// instead of the staff dashboard.
const Staff = ({ children, adminOnly = false }) => (
  <ProtectedRoute adminOnly={adminOnly}>{children}</ProtectedRoute>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <DraftJobProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/p/:token" element={<PublicProposal />} />
            <Route path="/q/:token" element={<SupplierRFQ />} />
            <Route path="/qtr/:token" element={<QuarterPublic />} />
            <Route path="/legal/eula" element={<LegalEula />} />
            <Route path="/legal/privacy" element={<LegalPrivacy />} />

            {/* Staff routes - every one gated by ProtectedRoute */}
            <Route path="/" element={<Navigate to="/share" replace />} />
            <Route path="/share" element={<Staff><ShareProposal /></Staff>} />
            <Route path="/exports" element={<Staff><Exports /></Staff>} />
            <Route path="/pipeline" element={<Staff><Pipeline /></Staff>} />
            <Route path="/revenue" element={<Staff adminOnly><Revenue /></Staff>} />
            <Route path="/quarter" element={<Staff adminOnly><QuarterGlance /></Staff>} />
            <Route path="/board" element={<Staff adminOnly><BoardPack /></Staff>} />
            <Route path="/announcements" element={<Staff adminOnly><Announcements /></Staff>} />
            <Route path="/activity" element={<Staff><Activity /></Staff>} />
            <Route path="/sow" element={<Navigate to="/sow/emails" replace />} />
            <Route path="/sow/preview" element={<Staff><SowPreview /></Staff>} />
            <Route path="/sow/emails" element={<Staff adminOnly><SowEmails /></Staff>} />
            <Route
              path="/team"
              element={<Staff adminOnly><Team /></Staff>}
            />
            <Route
              path="/share/:entry/rfqs"
              element={<Staff><StaffRfqs /></Staff>}
            />
            <Route
              path="/contract-review"
              element={<Staff><ContractReview /></Staff>}
            />
            <Route path="/deals" element={<Navigate to="/pipeline" replace />} />
            <Route
              path="/proposal-search"
              element={<Navigate to="/pipeline" replace />}
            />
          </Routes>
          </DraftJobProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
